import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik } from "formik";
import { useIntl, FormattedMessage } from "react-intl";
import * as yup from "yup";
import clsx from "clsx";

import {
  Box,
  Button,
  Grid,
  IconButton,
  Card,
  CardMedia,
  Tooltip,
  MenuItem,
  TextField,
  CircularProgress,
} from "@material-ui/core";
import { PhotoCamera } from "@material-ui/icons";
import {
  uiCloseModalEdit,
  uiCloseSuccessAlert,
} from "../../../redux/actions/uiActions";
import { MyTextField } from "../../custom/MyTextField";
import { useStyles } from "../../../shared/styles/useStyles";
import { AppState } from "../../../redux/reducers/rootReducer";
import {
  SUPPORTED_FORMATS_FILE,
  TypeDocUsers,
  TypeUser,
} from "../../../enums/enums";
import { MyAlert } from "../../custom/MyAlert";
import logo from "../../../assets/images/user.jpg";
import { IUserForm } from "../../../module/admin/form/IUsersForm";
import {
  startEditUser,
  userCleanActive,
} from "../../../module/admin/redux/actions/usersActions";
import ValidationForm from "../../../utils/uploadFile/form/ValidationForm";
import { fileHandleSelect } from "../../../utils/uploadFile/FileValidation";
import { IFileValidation } from "../../../utils/uploadFile/interfaces/IFileValidation";

export const FormEditUser = () => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();

  const [noValid, setNoValid] = useState(false);
  const [validationData, setValidationData] = useState<IFileValidation>({
    noValid: false,
    noValidSize: false,
    noValidName: false,
    ok: false,
  });

  const [labelImage, setLabelImage] = useState("");
  const { successAlert } = useSelector((state: AppState) => state.ui);
  const { user: entity } = useSelector((state: AppState) => state.auth);

  const { activeUser } = useSelector((state: AppState) => state.users);
  const user: any = activeUser;
  const [profileFile, setProfileFile] = useState<File | null>(
    user ? user.profileImage : ""
  );

  const validationSchema = yup.object({
    typeDoc: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    document: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`)
      .min(6, `${intl.formatMessage({ id: "MinimumDocument" })}`),
    firstName: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    secondName: yup.string(),
    firstLastName: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    secondLastName: yup.string(),
    username: yup.string(),
    email: yup
      .string()
      .email(`${intl.formatMessage({ id: "InvalidEmail" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    mobilePhone: yup
      .number()
      .typeError(`${intl.formatMessage({ id: "NumericValue" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    address: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    profileImage: yup.mixed(),
  });

  const initialValues: IUserForm = {
    id: user ? user.id : "",
    createdAt: user ? user.createdAt : "",
    typeDoc: user ? user.typeDoc : "",
    document: user ? user.document : "",
    firstName: user ? user.firstName : "",
    secondName: user ? user.secondName : "",
    firstLastName: user ? user.firstLastName : "",
    secondLastName: user ? user.secondLastName : "",
    username: "",
    email: user ? user.email : "",
    mobilePhone: user ? user.mobilePhone : "",
    address: user ? user.address : "",
    profileImage: "",
    active: user ? user.active : false,
    rol: TypeUser.OFFICER,
    restricUser: false,
    modules: user ? user.modules : [],
    idEntity: user ? user.idEntity : "",
    uid: user ? user.uid : "",
    businessName: user ? user.businessName : "",
    offices: user ? user.offices : [],
    country: entity ? entity.country : "",
    department: entity ? entity.department : "",
  };
  const onClose = () => {
    dispatch(uiCloseModalEdit());
    dispatch(userCleanActive());
  };

  const closeSuccess = async () => {
    dispatch(uiCloseSuccessAlert());
    dispatch(uiCloseModalEdit());
    dispatch(userCleanActive());
  };

  const handleSelectFile = (e: any) => {
    setNoValid(false);
    setLabelImage("");

    const file = e.target.files[0] as File;
    const now = new Date().getTime().toString();

    if (file) {
      let validation = fileHandleSelect(file);
      setValidationData(validation);

      if (validation.ok) {
        setNoValid(true);
        return;
      }

      setProfileFile(file);
      setLabelImage(now + "_" + e.target.files[0].name);
    }
  };

  return (
    <Box m={1}>
      <Formik
        validateOnChange={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={async (values, { setSubmitting }) => {
          if (!noValid) {
            setSubmitting(true);
            values.profileImage = profileFile;
            let changeImage = false;
            profileFile === user.profileImage
              ? (changeImage = false)
              : (changeImage = true);
            await dispatch(startEditUser(values, changeImage));
            setSubmitting(false);
          } else {
            setSubmitting(false);
          }
        }}
      >
        {({ values, isSubmitting }) => (
          <Form className={classes.input}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Grid item xs={12}>
                  <MyTextField
                    name="image"
                    variant="outlined"
                    label={<FormattedMessage id="Image" />}
                    className={classes.myTextFieldRoot}
                    disabled
                  />
                </Grid>

                <Grid item>
                  <Card className={classes.card}>
                    <CardMedia
                      className={classes.media}
                      image={
                        !user || !user.profileImage ? logo : user.profileImage
                      }
                      title="Image Perfil"
                    />
                  </Card>
                </Grid>

                <Grid container style={{ marginTop: "15px" }}>
                  <Grid item xs={11}>
                    <TextField
                      size="small"
                      type="file"
                      inputProps={{
                        accept: SUPPORTED_FORMATS_FILE.TYPE.join(","), // Convierte el array en un string separado por comas
                      }}
                      onChange={handleSelectFile}
                      id="icon-button-file"
                      autoComplete="off"
                      style={{ display: "none" }}
                    />

                    <MyTextField
                      disabled={true}
                      variant="outlined"
                      style={{ color: "black" }}
                      label={labelImage}
                      name="hola"
                      className={classes.myTextFieldRoot}
                    />
                  </Grid>

                  <Grid item xs={1}>
                    <Box mt={1} ml={-1}>
                      <Tooltip
                        title={`${intl.formatMessage({ id: "SearchImage" })}`}
                      >
                        <label htmlFor="icon-button-file">
                          <IconButton component="span">
                            <PhotoCamera />
                          </IconButton>
                        </label>
                      </Tooltip>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <ValidationForm validationData={validationData} />
                  </Grid>
                </Grid>

                <Grid item xs={12} style={{ marginTop: "8px" }}>
                  <label className="form-text">
                    <FormattedMessage id="UserType" />
                  </label>
                  <MyTextField
                    name="userType"
                    variant="outlined"
                    className={classes.myTextFieldRoot}
                    label={`${intl.formatMessage({ id: "OFFICER" })}`}
                    disabled
                  />
                </Grid>
              </Grid>

              <Grid item xs={8}>
                <Grid item xs={12}>
                  <MyTextField
                    name="data"
                    variant="outlined"
                    label={<FormattedMessage id="Data" />}
                    className={classes.myTextFieldRoot}
                    disabled
                  />
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={6} style={{ marginTop: "10px" }}>
                    <label className="form-text">
                      <FormattedMessage id="FirstName" />*
                    </label>
                    <MyTextField
                      name="firstName"
                      variant="outlined"
                      className={classes.myTextFieldRoot}
                    />
                  </Grid>

                  <Grid item xs={6} style={{ marginTop: "10px" }}>
                    <label className="form-text">
                      <FormattedMessage id="SecondName" />
                    </label>
                    <MyTextField
                      name="secondName"
                      variant="outlined"
                      className={classes.myTextFieldRoot}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <label className="form-text">
                      <FormattedMessage id="FirstLastName" />*
                    </label>
                    <MyTextField
                      name="firstLastName"
                      variant="outlined"
                      className={classes.myTextFieldRoot}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <label className="form-text">
                      <FormattedMessage id="SecondLastName" />
                    </label>
                    <MyTextField
                      name="secondLastName"
                      variant="outlined"
                      className={classes.myTextFieldRoot}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <label className="form-text">
                      <FormattedMessage id="UserName" />
                    </label>
                    <MyTextField
                      name="username"
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      className={classes.myTextFieldRoot}
                      label={`${values.firstName} ${values.secondName} ${values.firstLastName} ${values.secondLastName}`}
                      disabled={true}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <label className="form-text">
                      <FormattedMessage id="Email" />*
                    </label>
                    <MyTextField
                      name="email"
                      variant="outlined"
                      className={classes.myTextFieldRoot}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <label className="form-text">
                      <FormattedMessage id="DocumentType" />*
                    </label>
                    <MyTextField
                      name="typeDoc"
                      variant="outlined"
                      select
                      className={classes.myTextFieldRoot}
                    >
                      <MenuItem value={TypeDocUsers.CC}>
                        <FormattedMessage id="CitizenshipCard" />
                      </MenuItem>
                      <MenuItem value={TypeDocUsers.CE}>
                        <FormattedMessage id="ForeignersIdentityCard" />
                      </MenuItem>
                      <MenuItem value={TypeDocUsers.PASSPORT}>
                        <FormattedMessage id="Passport" />
                      </MenuItem>
                    </MyTextField>
                  </Grid>

                  <Grid item xs={6}>
                    <label className="form-text">
                      <FormattedMessage id="DocumentNumber" />*
                    </label>
                    <MyTextField
                      name="document"
                      variant="outlined"
                      disabled
                      className={classes.myTextFieldRoot}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <label className="form-text">
                      <FormattedMessage id="Mobile" />*
                    </label>
                    <MyTextField
                      name="mobilePhone"
                      variant="outlined"
                      className={classes.myTextFieldRoot}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <label className="form-text">
                      <FormattedMessage id="Address" />*
                    </label>
                    <MyTextField
                      name="address"
                      variant="outlined"
                      className={classes.myTextFieldRoot}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Box mt={2} display="flex" flexDirection="row-reverse">
              {!isSubmitting ? (
                <Button
                  className={clsx(classes.btn, classes.save)}
                  autoFocus
                  type="submit"
                  disabled={isSubmitting}
                >
                  <FormattedMessage id="Save" />
                </Button>
              ) : (
                <Button
                  className={clsx(classes.btn, classes.save)}
                  autoFocus
                  type="button"
                  disabled={true}
                >
                  <CircularProgress className={classes.btnLoading} />
                </Button>
              )}
              <Button
                className={clsx(classes.btn, classes.cancel)}
                onClick={onClose}
              >
                <FormattedMessage id="Cancel" />
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      <MyAlert
        open={successAlert}
        typeAlert="success"
        message="UpdatedUser"
        time={2000}
        handleClose={closeSuccess}
      />
    </Box>
  );
};
